import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import { get_feeds_list } from "src/DAL/Community/Community";
import { s3baseUrl } from "src/config/config";
import { htmlDecode } from "src/utils/convertHtml";
import { gr, tokens } from "src/design-system/tokens";
import GrStoryTile from "src/design-system/components/GrStoryTile";
import GrLoader from "src/design-system/components/GrLoader";
import GrText from "src/design-system/components/GrText";
import { FeedDetailDialog } from "./FeedDetailPopup";

/**
 * Dashboard Community Feed as a stories row: the latest posts as tall tiles, as many as fit in
 * one row (no scrolling, no load more). A tile opens the post popup, where the member likes and
 * comments; the card header's "Go To Community Feed" has the rest. Always the default (general)
 * feed: the group picked on the Community Feed page does not change the dashboard.
 */

const TILE_MIN = 132; // px: narrowest tile; the row holds as many as fit
const GAP = tokens.space[2]; // px, the grid gap below (gr.space[2])
const FETCH_LIMIT = 15;
const QUERY_KEY = "dashboardFeedStories";

// YouTube still for a video post (other video hosts have no public still: a light tile with the play mark).
const youtubeId = (url = "") =>
  url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([\w-]{11})/
  )?.[1];

const storyOf = (feed) => {
  const photo = feed.feed_type === "image" ? feed.image?.[0] : null;
  const isVideo = feed.feed_type === "video" && Boolean(feed.video_url);
  const ytId = isVideo ? youtubeId(feed.video_url) : null;
  let image = "";
  if (photo) image = s3baseUrl + (photo.thumbnail_2 || photo.thumbnail_1);
  else if (ytId) image = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  return {
    image,
    text: htmlDecode(feed.description || "").trim(),
    play: isVideo,
    name: htmlDecode(feed.created_by?.name || ""),
    avatarSrc: s3baseUrl + feed.created_by?.profile_image,
  };
};

// How many tiles fit across the element (re-measured when it resizes).
const useFitCount = (ref) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () =>
      setCount(Math.max(1, Math.floor((el.clientWidth + GAP) / (TILE_MIN + GAP))));
    measure();
    // Re-measured on the next frame, not inside the observer's own callback: the new tile count
    // changes the row's layout, which would otherwise trigger "ResizeObserver loop" errors.
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    if (observer) observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [ref]);
  return count;
};

export default function DashboardFeedStories() {
  const queryClient = useQueryClient();
  const rowRef = useRef(null);
  const count = useFitCount(rowRef);
  // The post stays set while the popup closes (its fade-out still shows it); only `postOpen` changes.
  const [openPost, setOpenPost] = useState(null);
  const [postOpen, setPostOpen] = useState(false);

  const { data: feeds = [], isLoading, isError } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const res = await get_feeds_list(`api/feed/group/feed_list?limit=${FETCH_LIMIT}&page=0`);
      if (res?.code !== 200) throw new Error(res?.message);
      return res.feeds || [];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  const showPost = (feed) => {
    setOpenPost(feed);
    setPostOpen(true);
  };
  const closePost = () => setPostOpen(false);
  const closeAndRefresh = () => {
    closePost();
    refresh();
  };

  const shown = feeds.slice(0, count);

  let body = null;
  if (isLoading) body = <GrLoader sx={{ py: gr.space[8] }} />;
  else if (isError) body = <GrText variant="secondary">Could not load posts. Please try again later.</GrText>;
  else if (!feeds.length) body = <GrText variant="secondary">No posts yet.</GrText>;
  else if (count) {
    body = (
      <Box
        component="ul"
        aria-label="Latest posts"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          display: "grid",
          gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
          gap: gr.space[2],
        }}
      >
        {shown.map((feed) => {
          const story = storyOf(feed);
          return (
            <li key={feed._id}>
              <GrStoryTile
                {...story}
                label={`Open post by ${story.name || "a member"}`}
                onClick={() => showPost(feed)}
              />
            </li>
          );
        })}
      </Box>
    );
  }

  return (
    <>
      <Box ref={rowRef} sx={{ width: "100%" }}>
        {body}
      </Box>

      {/* The same post popup as the Community Feed (× / Esc / backdrop close it; photos and videos
          open over the whole window). */}
      <FeedDetailDialog
        open={postOpen && Boolean(openPost)}
        onClose={closePost}
        post_id={openPost?._id}
        feed={openPost || {}}
        getFeedsListingOnAddFeed={refresh}
        handleSuccessDelete={closeAndRefresh}
        handleSuccessUpdate={closeAndRefresh}
        selectedImageIndex={0}
      />
    </>
  );
}
